// src/components/layout/NotificationPanel.tsx
// ============================================================
// SALT — Notification Dropdown Panel (Portaled)
//
// Positioned below the bell icon, rendered in document.body
// to avoid navbar stacking context issues (same pattern as
// UserMenuPortal and GameSelector dropdown).
//
// Shows:
//   - Header with "Notifications" title + "Mark All Read" action
//   - Scrollable list of NotificationItem rows
//   - Empty state when no notifications
//   - Loading skeleton while fetching
// ============================================================

import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import NotificationItem from './NotificationItem';
import { useOverlayFocus } from '../../hooks/useOverlayFocus';
import type { Notification } from '../../types/notification.types';

// ── Props ───────────────────────────────────────────────────

interface NotificationPanelProps {
  open: boolean;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  notifications: Notification[];
  isLoading: boolean;
  unreadCount: number;
  onMarkRead: (notificationId: number) => void;
  onMarkAllRead: () => void;
  onClearAll: () => void;
  onNavigate: (url: string) => void;
}

// ── Component ───────────────────────────────────────────────

export default function NotificationPanel({
  open,
  anchorRef,
  onClose,
  notifications,
  isLoading,
  unreadCount,
  onMarkRead,
  onMarkAllRead,
  onClearAll,
  onNavigate,
}: NotificationPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, right: 0 });

  useOverlayFocus(panelRef, open);

  // ── Position from anchor ──────────────────────────────────

  const computePosition = () => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({
      top: Math.round(rect.bottom + 8),
      right: Math.round(window.innerWidth - rect.right),
    });
  };

  useLayoutEffect(() => {
    if (open) computePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!open) return undefined;

    const handleReposition = () => computePosition();
    window.addEventListener('resize', handleReposition, { passive: true });
    window.addEventListener('scroll', handleReposition, { passive: true, capture: true });

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, { capture: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Close on outside click or Escape ──────────────────────

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        anchorRef.current &&
        !anchorRef.current.contains(target)
      ) {
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, anchorRef]);

  // ── Handle navigation from notification click ─────────────

  const handleNavigate = (url: string) => {
    onClose();
    onNavigate(url);
  };

  if (!open) return null;

  return createPortal(
    <div
      ref={panelRef}
      className="sl-notif-panel"
      style={{ top: `${pos.top}px`, right: `${pos.right}px` }}
      role="dialog"
      aria-label="Notifications"
      tabIndex={-1}
    >
      {/* ── Header ── */}
      <div className="sl-notif-panel-header">
        <span className="sl-notif-panel-title">Notifications</span>
        <div className="sl-notif-panel-actions">
          {unreadCount > 0 && (
            <button
              type="button"
              className="sl-notif-mark-all"
              onClick={onMarkAllRead}
            >
              Mark all read
            </button>
          )}
          {notifications.length > 0 && (
            <button
              type="button"
              className="sl-notif-mark-all sl-notif-clear-all"
              onClick={onClearAll}
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* ── Content ── */}
      <div className="sl-notif-panel-body">
        {isLoading ? (
          // Loading skeleton
          <div className="sl-notif-skeleton-list">
            {[1, 2, 3].map((i) => (
              <div key={i} className="sl-notif-skeleton">
                <div className="sl-notif-skeleton-icon" />
                <div className="sl-notif-skeleton-text">
                  <div className="sl-notif-skeleton-line sl-notif-skeleton-line--title" />
                  <div className="sl-notif-skeleton-line sl-notif-skeleton-line--msg" />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          // Empty state
          <div className="sl-notif-empty">
            <svg
              className="sl-notif-empty-icon"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 01-3.46 0" />
            </svg>
            <span className="sl-notif-empty-text">You're all caught up</span>
          </div>
        ) : (
          // Notification list
          <div className="sl-notif-list">
            {notifications.map((n) => (
              <NotificationItem
                key={n.notificationId}
                notification={n}
                onMarkRead={onMarkRead}
                onNavigate={handleNavigate}
              />
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}